import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

const KEY = 'apav-a11y'
const defaults = {
  fontSize: 0,        // 0 normal · 1 grande · 2 maior
  contrast: false,
  grayscale: false,
  highlightLinks: false,
  pauseAnimations: false,
}

export default function AccessibilityWidget({ open, onClose }) {
  const { t } = useTranslation()
  const [s, setS] = useState(() => {
    try {
      return { ...defaults, ...JSON.parse(localStorage.getItem(KEY)) }
    } catch {
      return defaults
    }
  })
  const panelRef = useRef(null)
  const closeBtnRef = useRef(null)

  /* Aplica as definições ao <html> */
  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(s))
    const html = document.documentElement
    const sizes = ['', '112%', '125%']
    html.style.fontSize = sizes[s.fontSize] || ''
    html.classList.toggle('a11y-contrast', s.contrast)
    html.classList.toggle('a11y-grayscale', s.grayscale)
    html.classList.toggle('a11y-highlight-links', s.highlightLinks)
    html.classList.toggle('a11y-pause-animations', s.pauseAnimations)
  }, [s])

  /* Fecha com Escape + foca o botão de fechar ao abrir */
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKeyDown)
    const t = setTimeout(() => { closeBtnRef.current?.focus() }, 50)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      clearTimeout(t)
    }
  }, [open, onClose])

  const toggle = (key) => setS(prev => ({ ...prev, [key]: !prev[key] }))
  const reset  = () => setS(defaults)

  if (!open) return null

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('a11y.panelAria')}
      aria-modal="true"
      className="a11y-panel shadow-2xl"
    >
      <div className="a11y-panel__header">
        <span className="a11y-panel__title" id="a11y-title">{t('a11y.title')}</span>
        <button
          ref={closeBtnRef}
          onClick={onClose}
          aria-label={t('a11y.close')}
          className="a11y-panel__close focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1"
        >
          ✕
        </button>
      </div>

      <div className="a11y-panel__body">
        <div className="a11y-panel__section">
          <span className="a11y-panel__label" id="font-size-label">{t('a11y.fontSize')}</span>
          <div className="a11y-panel__row" role="group" aria-labelledby="font-size-label">
            {['fontNormal', 'fontLarge', 'fontLarger'].map((chave, i) => (
              <button
                key={i}
                onClick={() => setS(prev => ({ ...prev, fontSize: i }))}
                className={`a11y-panel__step${s.fontSize === i ? ' active' : ''}`}
                aria-pressed={s.fontSize === i}
              >
                {t(`a11y.${chave}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="a11y-panel__section mt-2">
          <span className="a11y-panel__label">{t('a11y.optionsLabel')}</span>
          <div className="flex flex-col gap-2">
            {[
              { key: 'contrast',        chave: 'contrast' },
              { key: 'grayscale',       chave: 'grayscale' },
              { key: 'highlightLinks',  chave: 'highlightLinks' },
              { key: 'pauseAnimations', chave: 'pauseAnimations' },
            ].map(({ key, chave }) => (
              <button
                key={key}
                role="switch"
                aria-checked={s[key]}
                onClick={() => toggle(key)}
                className={`a11y-panel__toggle${s[key] ? ' active' : ''}`}
              >
                <span className="a11y-panel__toggle-dot" />
                <span>{t(`a11y.${chave}`)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="a11y-panel__footer">
        <button
          onClick={reset}
          className="a11y-panel__reset hover:bg-slate-50"
          aria-label={t('a11y.resetAria')}
        >
          {t('a11y.reset')}
        </button>
      </div>
    </div>
  )
}
